import "./App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { supabase } from "./services/supabase";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppRouter } from "./router";
import { ErrorBoundary } from "react-error-boundary";
import { UnknownErrorFallback } from "@/components/UnknownErrorDialog";
import { useIsLoading, setSession, setLoading } from "./store";
import { FinancialLoadingScreen } from "./components/loading/FinancialLoadingScreen";

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : null;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const isLoading = useIsLoading();

  useEffect(() => {
    // Check for active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    // Set up auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // If user logs out or session becomes null, invalidate all queries
      if (event === "SIGNED_OUT" || session === null) {
        queryClient.clear();
      }
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (isLoading) {
    return <FinancialLoadingScreen />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary
          FallbackComponent={UnknownErrorFallback}
          onReset={() => {
            queryClient.clear();
          }}
        >
          <AppRouter />
          {ReactQueryDevtools ? (
            <Suspense fallback={null}>
              <ReactQueryDevtools initialIsOpen={false} />
            </Suspense>
          ) : null}
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
